import React, { useState, useContext, useEffect, useCallback } from 'react'
import {
  AddButton,
  AddButtonImg,
  AddButtonText,
  AddProductContainer,
  BreadCrumble,
  Button,
  Card,
  CollapsibleHeader,
  FirstText,
  FormGrid,
  FormGroup,
  InfoLink,
  InfoName,
  InfoValue,
  InputText,
  Label,
  MoreInformation,
  SecondText,
  Select,
  Separator,
  ShortLabel,
  Summary,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  Title,
} from './style'
import { ChevronDown, ChevronUp, Info, Plus } from 'lucide-react'
import plus from '../../../../assets/fashion/TableFashionHeader/plus.svg'
import AddProductForm from '../AddProductForm'
import { useOutletContext } from 'react-router-dom'
import axios from 'axios'
import { ActionsDiv, ProductDiv, ProductShort } from '../AddProductForm/style'
import rectangle from '../../../../assets/fashion/TableFashionHeader/rectangle.svg'
import trash from '../../../../assets/fashion/TableFashionHeader/trash.svg'

import editPen from '../../../../assets/fashion/TableFashionHeader/edit-pen.svg' // Importar para o resumo do produto
import VendorSelect from '../SelectItem/VendorSelect'
import CompanySelect from '../SelectItem/CompanySelect'

interface FamilyData {
  id: number
  name: string
  groups?: GroupData[]
  colors?: { name: string }[]
}

interface GroupData {
  id: number
  name: string
  products?: any[]
}

interface FashionOrderProductItemInput {
  colorId: number
  size: string
  quantity: number
}

interface ProductApiData {
  fashionProductId: number
  discount: number | null
  totalItems: number
  subtotal: number
  items: FashionOrderProductItemInput[]
  fashionOrderId?: number
  fashionProduct?: {
    id: number
    name: string
    price: number
    size: string[]
    group: {
      id: number
      name?: string
      family?: {
        id: number
        name?: string
        collectionId?: number
      }
    }
  }
  colorCount?: number
  colors?: string[]
  family?: {
    id: number
    Corileone?: { name: string }
    name?: string
    familyId?: number
  }
  group?: { id: number; name?: string }
  name?: string
  price?: number
  totalQuantity?: number
  totalSubtotal?: number
  selectedColors?: number[]
  discountValue?: number
}

interface CompanyData {
  id: number
  companyName: string
}

interface CompanyItemType {
  id: number
  companyName: string
}

interface UserItemType {
  id: number;
  name: string;
}

interface CreateFashionOrderPayload {
  companyId: number;
  supplierId: number;
  sellerId: number;
  orderType: string;
  paymentCondition: string;
  shippingType: string;
  trackingCode: string;
  shippingCost: number;
  totalCost: number;
  startDateOfDelivery: string | null;
  finalDateOfDelivery: string | null;
  extraInformation: string;
  quantityItems: string;
  quantityPieces: string;
  deliveryTime: string;
  status: string;
  saleStatus: string;
  maximumBillingAmount: string;
  products: {
    fashionProductId?: number;
    groupId?: number;
    discount: number | null;
    totalItems: number;
    subtotal: number;
    items: {
      colorId: number;
      size: string;
      quantity: number;
    }[];
    colorCount?: number;
    colors?: string[];
    selectedColors?: number[];
  }[];
  attachmentFile?: string;
  attachmentFileName?: string;
}

type FashionFormContent = {
  handleSave: React.MutableRefObject<() => void>
}

const cleanCurrency = (value: any): number => {
  const stringValue = String(value || '')
  const number = Number(stringValue.replace(/[^0-9,-]+/g, '').replace(',', '.'))
  return isNaN(number) ? 0 : number
}

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatForInput = (digits: string): string => {
  if (digits === null || digits === undefined || digits === '') {
    return ''
  }

  const numericValue = Number(digits) / 100

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numericValue);
}

const calculateDiscountValue = (subtotal: number, discountPercentage: number): number => {
  return (subtotal * discountPercentage) / 100;
}

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = error => reject(error)
  })
}

const AddFashionOrder: React.FC = () => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(true)
  const [showAddProduct, setShowAddProduct] = useState(false)
  const [isInformationOpen, setIsInformationOpen] = useState(false)
  const [companyData, setCompanyData] = useState<CompanyData[]>([])
  const [supplierData, setSupplierData] = useState<CompanyData[]>([])
  const [addedProducts, setAddedProducts] = useState<ProductApiData[]>([])
  const [editingProductIndex, setEditingProductIndex] = useState<number | null>(
    null,
  )
  const [productToEdit, setProductToEdit] = useState<ProductApiData | null>(
    null,
  )
  const [shippingOptions, setShippingOptions] = useState<string[]>([])
  const [newshippingType, setNewshippingType] = useState('')
  const [paymentOptions, setPaymentOptions] = useState<string[]>([])
  const [newPaymentCondition, setNewPaymentCondition] = useState('')
  const [isAddingPayment, setIsAddingPayment] = useState(false)
  const [isAddingShipping, setIsAddingShipping] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [fileUploadStatus, setFileUploadStatus] = useState<string>("")

  const { handleSave } = useOutletContext<FashionFormContent>()
  const [formState, setFormState] = useState({
    supplier: { id: 0, companyName: '' } as CompanyItemType,
    seller: { id: 0, name: '' } as UserItemType,
    company: { id: 0, companyName: '' } as CompanyItemType,
    orderType: '',
    paymentCondition: '',
    shippingType: '',
    trackingCode: '',
    attachment: '',
    finalDateOfDelivery: '',
    startDateOfDelivery: '',
    extraInformation: '',
    quantityItems: '0',
    quantityPieces: '0',
    totalCost: 'R$ 0,00',
    deliveryTime: '',
    shippingCost: '',
    maximumBillingAmount: '',
  })

  const handleChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
  ) => {
    const { name, value } = e.target
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null
    const MAX_FILE_SIZE_BYTES = 200 * 1024 * 1024 // 200MB

    if (file && file.size > MAX_FILE_SIZE_BYTES) {
      console.error('O arquivo selecionado excede o limite de 200MB.')
      setSelectedFile(null)
      setFileName(null)
      setFileUploadStatus("Erro: Arquivo excede 200MB")
      event.target.value = ""
      alert("Tamanho do arquivo excede 200mb.")
      return
    }

    setSelectedFile(file)
    setFileName(file ? file.name : null)
  }

  const handleAddshippingType = async () => {
    if (newshippingType && !shippingOptions.includes(newshippingType)) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/shipping-types`,
          {
            name: newshippingType,
          },
        )
        if (response.status === 201) {
          setShippingOptions([...shippingOptions, newshippingType])
          setFormState({ ...formState, shippingType: newshippingType })
          setNewshippingType('')
          setIsAddingShipping(false)
        } else {
          console.error(
            'Erro ao salvar o novo tipo de frete:',
            response.statusText,
          )
          alert('Erro ao salvar o novo tipo de frete. Tente novamente.')
        }
      } catch (error) {
        console.error('Erro ao conectar com o backend:', error)
        alert(
          'Não foi possível conectar com o servidor para salvar o novo tipo de frete.',
        )
      }
    }
  }

  const handleAddPaymentCondition = async () => {
    if (newPaymentCondition && !paymentOptions.includes(newPaymentCondition)) {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/payment-conditions`,
          { name: newPaymentCondition },
        )

        if (response.status === 201) {
          setPaymentOptions([...paymentOptions, newPaymentCondition])
          setFormState({ ...formState, paymentCondition: newPaymentCondition })
          setNewPaymentCondition('')
          setIsAddingPayment(false)
        } else {
          console.error(
            'Erro ao salvar a nova condição de pagamento:',
            response.statusText,
          )
          alert('Erro ao salvar a nova condição de pagamento. Tente novamente.')
        }
      } catch (error) {
        console.error('Erro ao conectar com o backend:', error)
        alert(
          'Não foi possível conectar com o servidor para salvar a nova condição de pagamento.',
        )
      }
    }
  }

  const handleRemoveProductParent = (indexToRemove: number) => {
    setAddedProducts((prevProducts) => {
      const newProducts = [...prevProducts]
      newProducts.splice(indexToRemove, 1)
      return newProducts
    })
    setEditingProductIndex(null)
    setProductToEdit(null)
  }

  const handleEditProduct = (product: ProductApiData, index: number) => {
    setProductToEdit(product)
    setEditingProductIndex(index)
    setShowAddProduct(true)
  }

  const handleProductUpdated = (
    oldProduct: ProductApiData,
    updatedProduct: ProductApiData,
  ) => {
    if (editingProductIndex !== null) {
      const productWithColorCount = {
        ...updatedProduct,
        colorCount:
          updatedProduct.colorCount ||
          (updatedProduct.items && Array.isArray(updatedProduct.items)
            ? new Set(updatedProduct.items.map((item) => item.colorId)).size
            : 0),
      }

      setAddedProducts((prevProducts) => {
        const newProducts = [...prevProducts]
        const existingProduct = newProducts[editingProductIndex]
        newProducts[editingProductIndex] = {
          ...existingProduct,
          ...productWithColorCount,
          items: productWithColorCount.items || existingProduct.items || [],
          totalItems:
            productWithColorCount.totalItems ||
            (productWithColorCount.items
              ? productWithColorCount.items.reduce(
                (sum, item) => sum + (item.quantity || 0),
                0,
              )
              : existingProduct.totalItems),
          subtotal: productWithColorCount.subtotal || existingProduct.subtotal,
        }

        return newProducts
      })

      setProductToEdit(null)
      setEditingProductIndex(null)
      setShowAddProduct(false)
    }
  }

  const handleNewProductAdded = (newProduct: ProductApiData) => {
    const productWithColorCount = {
      ...newProduct,
      colorCount:
        newProduct.colorCount ||
        (newProduct.items && Array.isArray(newProduct.items)
          ? new Set(newProduct.items.map((item) => item.colorId)).size
          : 0),
    }

    setAddedProducts((prevProducts) => [...prevProducts, productWithColorCount])
    setShowAddProduct(false)
  }

  const handleSupplierChange = (supplier: CompanyItemType) => {
    setFormState((prevState) => ({ ...prevState, supplier }))
  }

  const handleCompanyChange = (company: CompanyItemType) => {
    setFormState((prevState) => ({ ...prevState, company }))
  }

  const handleSellerChange = (seller: UserItemType) => {
    setFormState((prevState) => ({ ...prevState, seller }))
  }

  const handleSaveOrder = useCallback(async () => {
    try {
      // 1. Agrupar produtos por fashionProductId para evitar duplicação
      const groupedProductsMap = new Map()

      addedProducts.forEach((product) => {
        const productId = product.fashionProduct?.id || product.fashionProductId

        if (productId === undefined) {
          console.error('Produto sem ID, ignorando...')
          return
        }

        if (groupedProductsMap.has(productId)) {
          const existingProduct = groupedProductsMap.get(productId)
          existingProduct.items = [
            ...(existingProduct.items || []),
            ...(product.items || []),
          ]
          existingProduct.totalItems += product.totalItems || 0
          existingProduct.subtotal += product.subtotal || 0
        } else {
          groupedProductsMap.set(productId, { ...product })
        }
      })

      const productsToSend = Array.from(groupedProductsMap.values())

      const formattedStartDate = formState.startDateOfDelivery
        ? `${formState.startDateOfDelivery}T00:00:00.000Z`
        : null
      const formattedFinalDate = formState.finalDateOfDelivery
        ? `${formState.finalDateOfDelivery}T00:00:00.000Z`
        : null

      const payload: CreateFashionOrderPayload = {
        companyId: formState.company.id,
        supplierId: formState.supplier.id,
        sellerId: formState.seller.id,
        orderType: formState.orderType,
        paymentCondition: formState.paymentCondition,
        shippingType: formState.shippingType,
        trackingCode: formState.trackingCode,
        shippingCost: cleanCurrency(formState.shippingCost),
        totalCost: cleanCurrency(formState.totalCost),
        startDateOfDelivery: formattedStartDate,
        finalDateOfDelivery: formattedFinalDate,
        extraInformation: formState.extraInformation,
        quantityItems: formState.quantityItems,
        quantityPieces: formState.quantityPieces,
        deliveryTime: formState.deliveryTime,
        status: "Em orçamento",
        saleStatus: "Em andamento",
        maximumBillingAmount: formState.maximumBillingAmount,
        products: productsToSend.map((product) => {
          let itemsToSend = product.items || []

          if (
            (!itemsToSend || itemsToSend.length === 0) &&
            product.colorCount &&
            product.colorCount > 0
          ) {
            if (
              product.selectedColors &&
              Array.isArray(product.selectedColors) &&
              product.selectedColors.length > 0
            ) {
              itemsToSend = product.selectedColors.map((colorId: any) => ({
                colorId: colorId,
                size: 'N/A',
                quantity: 0,
              }))
            } else if (
              product.colors &&
              Array.isArray(product.colors) &&
              product.colors.length > 0
            ) {
              itemsToSend = product.colors.map(
                (colorInfo: { id: any }, index: number) => ({
                  colorId:
                    typeof colorInfo === 'object' ? colorInfo.id : index + 1,
                  size: 'N/A',
                  quantity: 0,
                }),
              )
            } else {
              itemsToSend = Array.from(
                { length: product.colorCount },
                (_, index) => ({
                  colorId: index + 1,
                  size: 'N/A',
                  quantity: 0,
                }),
              )
            }
          }

          return {
            fashionProductId:
              product.fashionProduct?.id || product.fashionProductId,
            groupId: product.fashionProduct?.group?.id || product.group?.id,
            discount: product.discount,
            totalItems: product.totalItems,
            subtotal: product.subtotal,
            items: itemsToSend,
            colorCount: product.colorCount,
            colors: product.colors,
            selectedColors: product.selectedColors,
          }
        }),
      }

      if (selectedFile) {
        try {
          setFileUploadStatus("Convertendo arquivo...")
          const base64Data = await fileToBase64(selectedFile)
          payload.attachmentFile = base64Data
          payload.attachmentFileName = selectedFile.name
          setFileUploadStatus("Arquivo processado, enviando pedido...")
        } catch (error) {
          console.error('Erro ao converter arquivo para base64:', error)
          setFileUploadStatus("Erro ao processar arquivo")
          alert('Erro ao processar o arquivo anexo')
          return
        }
      }

      const response = await fetch(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/create-order`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      )

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro ao salvar pedido:', errorText);
        setFileUploadStatus("Erro ao salvar pedido")
        alert(`Erro ao salvar pedido: ${errorText}`);
        return;
      }
      const result = await response.json();
      alert('Pedido criado com sucesso!');
      setSelectedFile(null);
      setFileName(null);
      setTimeout(() => setFileUploadStatus(""), 3000);

    } catch (error) {
      console.error('Erro geral ao salvar pedido:', error);
      setFileUploadStatus("Erro geral ao salvar pedido")
      alert('Erro geral ao salvar pedido. Verifique o console para mais detalhes.');
    }
  }, [formState, addedProducts, selectedFile]);

  // Use useEffect para injetar a função no contexto
  useEffect(() => {
    handleSave.current = handleSaveOrder
  }, [handleSaveOrder])

  useEffect(() => {
    const fetchShippingOptions = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/shipping-types`,
        )
        if (response.status === 200) {
          const types = response.data.map((item: { name: string }) => item.name)
          setShippingOptions(types)
        }
      } catch (error) {
        console.error('Erro ao carregar os tipos de frete:', error)
      }
    }
    fetchShippingOptions()
  }, [])

  useEffect(() => {
    const fetchPaymentOptions = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-order/payment-conditions`,
        )
        if (response.status === 200) {
          const conditions = response.data.map(
            (item: { name: any }) => item.name,
          )
          setPaymentOptions(conditions)
        }
      } catch (error) {
        console.error('Erro ao carregar as condições de pagamento:', error)
      }
    }
    fetchPaymentOptions()
  }, [])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `https://companies-260311756054.southamerica-east1.run.app/companies?filters[0]=1&filters[1]=2`,
        )
        const data = response.data
        const clientes = data.filter((item: any) =>
          item.companyTypes?.includes('Cliente'),
        )
        const fornecedores = data.filter((item: any) =>
          item.companyTypes?.includes('Fornecedor'),
        )
        setCompanyData(clientes)
        setSupplierData(fornecedores)
      } catch (error: any) {
        console.error('Erro ao buscar dados:', error.message)
      }
    }
    fetchData()
  }, [])

  // Novo useEffect para calcular os valores dinamicamente
  useEffect(() => {
    let totalItemsCount = 0; // Contagem de produtos únicos
    let totalPiecesSum = 0; // Soma de todas as peças (totalItems de cada produto)
    let totalCostSum = 0; // Soma de todos os subtotais
    let totalDiscountValue = 0; // Valor total do desconto

    addedProducts.forEach(product => {
      totalItemsCount += 1;
      totalPiecesSum += Number(product.totalItems || 0);

      // Calcular subtotal sem desconto
      const subtotalWithoutDiscount = product.subtotal || 0;

      // Calcular valor do desconto se existir porcentagem de desconto
      let discountValue = 0;
      if (product.discount && product.discount > 0) {
        // Calcular o subtotal original (antes do desconto)
        const originalSubtotal = subtotalWithoutDiscount / (1 - product.discount / 100);
        discountValue = calculateDiscountValue(originalSubtotal, product.discount);
      }

      totalDiscountValue += discountValue;
      totalCostSum += subtotalWithoutDiscount;
    });

    setFormState((prevState) => ({
      ...prevState,
      quantityItems: String(totalItemsCount),
      quantityPieces: String(totalPiecesSum),
      totalCost: formatCurrency(totalCostSum),
    }));
  }, [addedProducts])

  const renderProductSummary = (product: ProductApiData, index: number) => {
    let familyName = '-'
    if (product.fashionProduct?.group?.family?.name) {
      familyName = product.fashionProduct.group.family.name
    } else if (product.family?.name) {
      familyName = product.family.name
    }

    let groupName = '-'
    if (product.fashionProduct?.group?.name) {
      groupName = product.fashionProduct.group.name
    } else if (product.group?.name) {
      groupName = product.group.name
    }

    const numberOfColorsToDisplay = (() => {
      if (product.colorCount !== undefined && product.colorCount !== null && product.colorCount > 0) {
        return product.colorCount
      }

      if (product.items && Array.isArray(product.items) && product.items.length > 0) {
        const uniqueColorIds = new Set(product.items.map((item) => item.colorId))
        return uniqueColorIds.size
      }

      if (product.colors && Array.isArray(product.colors) && product.colors.length > 0) {
        return product.colors.length
      }

      return 0
    })()

    const getDiscountValue = (): string => {
      if (!product.discount || product.discount <= 0) {
        return "-";
      }

      if (product.discountValue !== undefined) {
        return formatCurrency(product.discountValue);
      }

      const subtotal = product.subtotal || 0;
      const originalSubtotal = subtotal / (1 - product.discount / 100);
      const discountValue = calculateDiscountValue(originalSubtotal, product.discount);

      return formatCurrency(discountValue);
    };

    const productFields = [
      {
        label: 'Produto',
        value: product.fashionProduct?.name ?? '-',
        showIcon: true,
      },
      { label: 'Coleção', value: familyName, showIcon: true },
      { label: 'Cores', value: numberOfColorsToDisplay, showIcon: true },
      {
        label: 'Precificação',
        value:
          typeof product.fashionProduct?.price === 'number'
            ? `R$ ${product.fashionProduct.price.toFixed(2).replace('.', ',')}`
            : '-',
        showIcon: true,
      },
      {
        label: 'Quantidade de peças',
        value: product.totalItems ?? '-',
        showIcon: true,
      },
      {
        label: "Desconto",
        value: getDiscountValue(),
        showIcon: false,
      },
      {
        label: "Subtotal",
        value: typeof product.subtotal === "number" ? formatCurrency(product.subtotal) : "-",
        showIcon: false,
      },
    ]

    return (
      <Card key={product.fashionProductId || index}>
        <ProductShort>
          <ProductDiv>
            {productFields.map(({ label, value, showIcon }, i) => (
              <ShortLabel key={i}>
                <span style={{ fontWeight: 'normal' }}>{label}:</span> {value}
                {showIcon && (
                  <img
                    style={{ marginLeft: '0.5rem' }}
                    src={rectangle || '/placeholder.svg'}
                    alt=''
                  />
                )}
              </ShortLabel>
            ))}

            <ActionsDiv className='flex items-center gap-2'>
              <img
                src={trash || '/placeholder.svg'}
                alt='Remover'
                style={{ cursor: 'pointer' }}
                onClick={() => handleRemoveProductParent(index)}
              />
              <img
                src={editPen || '/placeholder.svg'}
                alt='Editar'
                style={{ cursor: 'pointer' }}
                onClick={() => handleEditProduct(product, index)}
              />
            </ActionsDiv>
          </ProductDiv>
        </ProductShort>
      </Card>
    )
  }

  return (
    <>
      <BreadCrumble>
        <FirstText>Pedidos fashion</FirstText>
        <SecondText>&gt; Novo pedido</SecondText>
      </BreadCrumble>

      <Card>
        <FormGrid>
          <FormGroup>
            <CompanySelect
              label='Fornecedor'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate/utils/companies`}
              displayField='companyName'
              queryParams={{ companyType: 2 }} // Supplier
              value={formState.supplier}
              onChange={handleSupplierChange}
            />
            <InfoLink>
              <Info size={16} />
              <div
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <span onClick={() => setIsInformationOpen(!isInformationOpen)}>
                  Mais informações
                </span>
                {isInformationOpen && (
                  <MoreInformation>
                    <InfoName>
                      Fantasia: <InfoValue>Super Fornecedor</InfoValue>
                    </InfoName>
                    <InfoName>
                      CNPJ: <InfoValue>12.345.678/0001-10</InfoValue>
                    </InfoName>
                    <InfoName>
                      Contato: <InfoValue>José da Silva</InfoValue>
                    </InfoName>
                    <InfoName>
                      Cidade/UF: <InfoValue>São Paulo/SP</InfoValue>
                    </InfoName>
                    <InfoName>
                      Telefone: <InfoValue>(12)98765-4321</InfoValue>
                    </InfoName>
                    <InfoName>
                      Email: <InfoValue>email@email.com</InfoValue>
                    </InfoName>
                  </MoreInformation>
                )}
              </div>
            </InfoLink>
          </FormGroup>
          <FormGroup>
            <VendorSelect
              label='Vendedor'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/backend/user/findAll`}
              displayField='name'
              value={formState.seller}
              onChange={handleSellerChange}
            />
            <InfoLink>
              <Info size={16} />
              <span>Mais informações</span>
            </InfoLink>
          </FormGroup>

          <FormGroup>
            <CompanySelect
              label='Cliente'
              apiUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/certificates/certificate/utils/companies`}
              displayField='companyName'
              queryParams={{ companyType: 1 }} // Client
              value={formState.company}
              onChange={handleCompanyChange}
            />
            <InfoLink>
              <Info size={16} />
              <span>Mais informações</span>
            </InfoLink>
          </FormGroup>
        </FormGrid>

        <Separator />

        <CollapsibleHeader onClick={() => setIsDetailsOpen(!isDetailsOpen)}>
          <Title>Detalhes do pedido</Title>
          <Button>
            {isDetailsOpen ? (
              <ChevronUp size={12} />
            ) : (
              <ChevronDown size={12} />
            )}
          </Button>
        </CollapsibleHeader>

        {isDetailsOpen && (
          <>
            <FormGrid style={{ marginTop: '16px' }}>
              <FormGroup>
                <Label>Tipo de pedido</Label>
                <Select
                  value={formState.orderType}
                  onChange={(e) =>
                    setFormState({ ...formState, orderType: e.target.value })
                  }
                >
                  <option value=''>Selecione</option>
                  <option value='Venda'>Venda</option>
                  <option value='Reposição'>Reposição</option>
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Data inicio da entrega</Label>
                <InputText
                  type='date'
                  value={formState.startDateOfDelivery}
                  max='9999-12-31'
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      startDateOfDelivery: e.target.value,
                    })
                  }
                  className='w-full rounded bg-gray-100 p-2' // Opcional: para manter o estilo do Select
                />
              </FormGroup>

              <FormGroup>
                <Label>Data final da entrega</Label>
                <InputText
                  type='date'
                  value={formState.finalDateOfDelivery}
                  max='9999-12-31'
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      finalDateOfDelivery: e.target.value,
                    })
                  }
                  className='w-full rounded bg-gray-100 p-2' // Opcional: para manter o estilo do Select
                />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>Condição de pagamento</Label>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {!isAddingPayment ? (
                    <Select
                      name='paymentCondition'
                      value={formState.paymentCondition}
                      onChange={handleChange}
                      style={{ flexGrow: 1 }}
                    >
                      <option value=''>
                        Selecione a condição de pagamento
                      </option>
                      {paymentOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <InputText
                      type='text'
                      placeholder='Digite a nova condição'
                      value={newPaymentCondition}
                      onChange={(e) => setNewPaymentCondition(e.target.value)}
                      onBlur={handleAddPaymentCondition}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAddPaymentCondition()
                          setIsAddingPayment(false)
                        }
                      }}
                      autoFocus
                      style={{ flexGrow: 1 }}
                    />
                  )}
                  <Plus
                    size={16}
                    onClick={() => setIsAddingPayment(!isAddingPayment)}
                    style={{ cursor: 'pointer', minWidth: '16px' }}
                  />
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Tipo de frete</Label>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  {!isAddingShipping ? (
                    <Select
                      name='shippingType'
                      value={formState.shippingType}
                      onChange={handleChange}
                      style={{ flexGrow: 1 }}
                    >
                      <option value=''>Selecione o tipo de frete</option>
                      {shippingOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <InputText
                      type='text'
                      placeholder='Digite o novo tipo de frete'
                      value={newshippingType}
                      onChange={(e) => setNewshippingType(e.target.value)}
                      onBlur={handleAddshippingType}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleAddshippingType()
                          setIsAddingShipping(false)
                        }
                      }}
                      autoFocus
                      style={{ flexGrow: 1 }}
                    />
                  )}
                  <Plus
                    size={16}
                    onClick={() => setIsAddingShipping(!isAddingShipping)}
                    style={{ cursor: 'pointer', minWidth: '16px' }}
                  />
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Valor do frete</Label>
                <InputText
                  type='text'
                  value={formatForInput(formState.shippingCost)}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\D/g, '')
                    setFormState({ ...formState, shippingCost: cleanedValue })
                  }}
                  placeholder='Digite o valor do frete'
                />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>Código de rastreio</Label>
                <InputText
                  type='text'
                  value={formState.trackingCode}
                  onChange={(e) =>
                    setFormState({ ...formState, trackingCode: e.target.value })
                  }
                  placeholder='Digite o código de rastreio'
                />
              </FormGroup>

              <FormGroup>
                <Label>Anexo</Label>
                <InputText
                  type="file"
                  onChange={handleFileChange}
                  accept="*/*"
                  style={{ marginBottom: '8px' }}
                />
                {fileName && (
                  <div style={{
                    fontSize: '12px',
                    color: '#7f8c8d',
                    marginTop: '4px'
                  }}>
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Valor máximo de faturamento</Label>
                <InputText
                  type='text'
                  value={formatForInput(formState.maximumBillingAmount)}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\D/g, '')
                    setFormState({
                      ...formState,
                      maximumBillingAmount: cleanedValue,
                    })
                  }}
                  className='w-full rounded bg-gray-100 p-2'
                  placeholder='Digite o valor máximo'
                />
              </FormGroup>
            </FormGrid>

            <FormGrid>
              <FormGroup>
                <Label>Informações adicionais</Label>
                <InputText
                  type='text'
                  value={formState.extraInformation}
                  onChange={(e) =>
                    setFormState({
                      ...formState,
                      extraInformation: e.target.value,
                    })
                  }
                />
              </FormGroup>
            </FormGrid>
          </>
        )}

        <Separator />

        <Summary>
          <SummaryItem>
            <SummaryLabel>Quantidade de itens:</SummaryLabel>
            <SummaryValue>{formState.quantityItems}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Quantidade de peças:</SummaryLabel>
            <SummaryValue>{formState.quantityPieces}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Total:</SummaryLabel>
            <SummaryValue>{formState.totalCost}</SummaryValue>
          </SummaryItem>
        </Summary>
      </Card>

      {/* Resumo dos produtos adicionados (agora visível na criação de pedido) */}
      {Array.isArray(addedProducts) && addedProducts.length > 0 && (
        <>
          {addedProducts.map((product, index) =>
            renderProductSummary(product, index),
          )}
        </>
      )}

      <AddProductContainer>
        {showAddProduct && (
          <AddProductForm
            onRemoveProduct={handleRemoveProductParent}
            onProductAdded={
              editingProductIndex === null ? handleNewProductAdded : undefined
            }
            onProductUpdated={
              editingProductIndex !== null ? handleProductUpdated : undefined
            }
            product={productToEdit}
            isEditing={editingProductIndex !== null}
            onClose={() => {
              setShowAddProduct(false)
              setEditingProductIndex(null)
              setProductToEdit(null)
            }}
            order={[]}
            setOrder={function (value: any): void {
              throw new Error('Function not implemented.')
            }}
          />
        )}
        <AddButton
          onClick={() => {
            setEditingProductIndex(null)
            setProductToEdit(null)
            setShowAddProduct(true)
          }}
        >
          <AddButtonImg src={plus} />
          <AddButtonText>Adicionar produto</AddButtonText>
        </AddButton>
      </AddProductContainer>
    </>
  )
}

export default AddFashionOrder
